import React from 'react';
import { StyleSheet, View, Text, FlatList, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import ReportTags from './ReportTags';

/**
 * Componente para mostrar una lista de reportes
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.reports - Lista de reportes
 * @param {Function} props.onReportPress - Función a ejecutar cuando se presiona un reporte
 */
const ReportList = ({ reports, onReportPress }) => {
  const navigation = useNavigation();
  const { user } = useSelector(state => state.auth);
  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Renderizar un reporte
  const renderReport = ({ item }) => {
    return (
      <TouchableOpacity 
        style={styles.reportItem}
        onPress={() => onReportPress(item)}
      >
        <View style={styles.reportHeader}>
          <View style={styles.titleSection}>
            <Text style={styles.missionTitle}>Misión #{item.mission_name}</Text>
            <Text style={styles.reportId}>{`Reporte #${item.id}`}</Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusText}>{getStatusText(item.status)}</Text>
          </View>
        </View>
        
        <ReportTags 
          tags={item.tags} 
          onTagPress={(tag) => console.log('Tag pressed:', tag)}
          style={styles.tagsContainer}
        />
        
        <View style={styles.infoRow}>
          <View style={styles.iconContainer}>
            <Ionicons name="calendar-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>{'Fecha:'}</Text>
          <Text style={styles.infoValue}>{formatDate(item.created_at)}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconContainer}>
            <Ionicons name="location-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>{'Ubicación:'}</Text>
          <Text style={styles.infoValue}>{item.location_name || 'No especificada'}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconContainer}>
            <Ionicons name="flag-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>{'Objetivo:'}</Text>
          <Text style={[styles.infoValue, !item.objectives_details || item.objectives_details.length === 0 ? styles.criticalText : null]}>
            {item.objectives_details && item.objectives_details.length > 0 
              ? item.objectives_details.map(obj => obj.name).join(', ')
              : 'Sin objetivo'
            }
          </Text>
        </View>
        
        <View style={styles.infoRow}>
          <View style={styles.iconContainer}>
            <Ionicons name="person-outline" size={18} color="#7f8c8d" />
          </View>
          <Text style={styles.infoLabel}>{'Piloto:'}</Text>
          {item.pilot_name && item.mission ? (
            <TouchableOpacity 
              onPress={(e) => {
                e.stopPropagation(); // Evitar que se propague al onPress del reporte
                navigation.navigate('PilotProfile', {
                  pilotId: item.pilot_id,
                  missionId: item.mission,
                  pilotName: item.pilot_name
                });
              }}
            >
              <Text style={[styles.infoValue, styles.clickableText]}>
                {item.pilot_name}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.infoValue}>No asignado</Text>
          )}
        </View>
        
        <View style={styles.reportFooter}>
          <Text style={styles.reportUpdate}>{`Última Actualización: ${formatDate(item.updated_at || item.created_at)}`}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  // Obtener el color según el estado del reporte
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'REJECTED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };

  // Obtener el texto según el estado del reporte
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_PROGRESS':
        return 'En Progreso';
      case 'COMPLETED':
        return 'Completado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return 'Desconocido';
    }
  };

  return (
    <View style={styles.container}>
      {reports.length === 0 ? (
        <Text style={styles.emptyText}>{'No hay reportes disponibles'}</Text>
      ) : (
        <View style={styles.listContainer}>
          <FlatList
            data={reports}
            keyExtractor={(item) => item.id.toString()}
            renderItem={renderReport}
            style={styles.list}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  emptyText: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontStyle: 'italic',
    padding: 20,
  },
  reportItem: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  titleSection: {
    flex: 1,
    marginRight: 10,
  },
  missionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
  },
  reportId: {
    fontSize: 14,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconContainer: {
    width: 24,
    alignItems: 'center',
    marginRight: 8,
  },
  infoLabel: {
    width: 80,
    fontSize: 14,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    fontSize: 14,
    color: '#2c3e50',
  },
  reportFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },

  reportUpdate: {
    fontSize: 12,
    color: '#777',
  },
  clickableText: {
    color: '#3498db',
    textDecorationLine: 'underline',
  },
  criticalText: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
  tagsContainer: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
});

export default ReportList;